<template>
	<div class="resource-planning-zoom-select">
		<SuiButton
			v-tooltip="$t('timeline_zoom_out')"
			:aria-label="$t('timeline_zoom_out')"
			type="secondary"
			class="resource-planning-zoom-select__icon-button"
			@click="zoomOut()"
		>
			<SuiIcon
				name="icon-zoomout-16"
				class="resource-planning-zoom-select__icon"
			/>
		</SuiButton>

		<VDropdown
			:distance="5"
			popper-class="resource-planning-zoom-select__dropdown"
			@show="isOpen = true"
			@apply-hide="isOpen = false"
		>
			<SuiButton
				class="resource-planning-zoom-select__selected-option"
				@click="showOptions()"
			>
				{{ selectedOption?.title }}

				<SuiIcon
					class="resource-planning-zoom-select__icon"
					:name="
						isOpen
							? 'icon-arrow-up-filled-16'
							: 'icon-arrow-down-filled-16'
					"
				/>
			</SuiButton>

			<template #popper>
				<div role="list">
					<SuiButton
						v-for="option in zoomOptions"
						:key="option.id"
						v-close-popper
						variant="unstyled"
						class="resource-planning-zoom-select__option-item"
						:class="{
							'resource-planning-zoom-select__option-item--selected':
								option.id === selectedOption?.id,
						}"
						@click="zoomToLevel(option)"
					>
						{{ option.title }}
					</SuiButton>
				</div>
			</template>
		</VDropdown>
		<SuiButton
			v-tooltip="$t('timeline_zoom_in')"
			:aria-label="$t('timeline_zoom_in')"
			class="resource-planning-zoom-select__icon-button"
			@click="zoomIn()"
		>
			<SuiIcon
				class="resource-planning-zoom-select__icon"
				name="icon-zoomin-16"
			/>
		</SuiButton>
	</div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { SuiIcon, SuiButton } from '@developers/scoro-ui';
import { type SuiListItemData } from '@/types';
import { useTranslation } from '@/composables';
import { ZOOM_OPTIONS, ZOOM_SCALE_OPTIONS } from '../../constants';

export interface ResourcePlanningZoomSelectProps {
	zoomLevel: number;
}

const props = defineProps<ResourcePlanningZoomSelectProps>();

const { t } = useTranslation();
const isOpen = ref(false);

const zoomOptions = computed<SuiListItemData[]>(() => [
	{
		title: t('days'),
		id: ZOOM_OPTIONS.DAY,
	},
	{
		title: t('weeks_plural'),
		id: ZOOM_OPTIONS.WEEK,
	},
	{
		title: t('months'),
		id: ZOOM_OPTIONS.MONTH,
	},
]);

const emit = defineEmits<{
	(e: 'zoom-in'): void;
	(e: 'zoom-out'): void;
	(e: 'zoom-to', id: string): void;
}>();

const showOptions = () => {
	isOpen.value = true;
};

const selectedOption = computed<SuiListItemData | undefined>(() =>
	zoomOptions.value.find(
		(option) => option.id === ZOOM_SCALE_OPTIONS[props.zoomLevel]
	)
);

const zoomOut = () => {
	emit('zoom-out');
};

const zoomIn = () => {
	emit('zoom-in');
};

const zoomToLevel = (option: SuiListItemData) => {
	emit('zoom-to', option.id);
};
</script>

<style lang="scss">
.resource-planning-zoom-select {
	&__dropdown {
		--popper-wrapper-min-width: auto;
	}
}
</style>

<style lang="scss" scoped>
@import '../ResourcePlanning/resourcePlanning';

.resource-planning-zoom-select {
	display: flex;

	&__icon-button {
		@extend %resource-planning-icon-button;

		&:first-child {
			border-radius: 0.5rem 0 0 0.5rem;
		}

		&:last-child {
			border-radius: 0 0.5rem 0.5rem 0;
		}
	}

	&__selected-option {
		@extend %resource-planning-icon-button;

		min-width: 15rem;
		overflow: hidden;
		height: 100%;
		aspect-ratio: unset;
		padding: 0 1rem;
		border-radius: 0;
		border-left: 1px solid $neutral-30;
		border-right: 1px solid $neutral-30;
	}

	&__option-item {
		--sui-button-hover-background-color: #{$neutral-10};

		width: 100%;
		text-align: center;
		border-radius: 0;
		font-weight: $scoro-fw-normal;

		&--selected {
			font-weight: $scoro-fw-semi-bold;
		}
	}
}
</style>
